import classNames from 'classnames';
import {
  Fragment,
  ReactNode,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CustomColumnType } from '../../../interfaces';

/**
 * Class name that marks a column as a row-actions column (the "⋮" button /
 * dropdown column every management table has).
 */
export const ACTIONS_COLUMN_CLASS_NAME = 'actions';

type AnyColumn = CustomColumnType<any>;

const isActionsColumn = (column: AnyColumn) =>
  (column.className ?? '').split(/\s+/).includes(ACTIONS_COLUMN_CLASS_NAME);

const getActionsColumnId = (column: AnyColumn, index: number) =>
  `custom-table-actions-${String(
    column.key ?? column.dataIndex ?? index,
  ).replace(/[^\w-]/g, '-')}`;

const getCellValue = (record: any, dataIndex: AnyColumn['dataIndex']) => {
  if (dataIndex === undefined || dataIndex === null) return undefined;
  const path = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  return path.reduce((value, key) => value?.[key], record);
};

const renderActionsCell = (
  column: AnyColumn,
  record: any,
  index: number,
): ReactNode => {
  if (!column.render) return null;

  const rendered = column.render(
    getCellValue(record, column.dataIndex),
    record,
    index,
  ) as ReactNode | { children?: ReactNode };

  // Legacy antd `render` return shape: `{ children, props }`.
  if (
    rendered &&
    typeof rendered === 'object' &&
    !isValidElement(rendered) &&
    'children' in rendered
  ) {
    return rendered.children;
  }

  return rendered as ReactNode;
};

const hasRenderedContent = (node: Element) =>
  node.childElementCount > 0 || !!node.textContent?.trim();

const observeContent = (node: Element, onMutate: () => void) => {
  const observer = new MutationObserver(onMutate);
  observer.observe(node, {
    childList: true,
    subtree: true,
    characterData: true,
  });
  return () => observer.disconnect();
};

type HiddenActionsColumnProbeProps = {
  column: AnyColumn;
  actionsColumnId: string;
  dataSource: readonly any[];
  onContentFound: (actionsColumnId: string) => void;
};

/**
 * Renders a hidden actions column's cells off-screen, purely to notice when
 * any row starts producing an action again (e.g. permissions finished
 * loading, or a new page of data came in) so the column can be shown again.
 */
const HiddenActionsColumnProbe = ({
  column,
  actionsColumnId,
  dataSource,
  onContentFound,
}: HiddenActionsColumnProbeProps) => {
  const probeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const probe = probeRef.current;
    if (!probe) return;

    const checkForContent = () => {
      if (hasRenderedContent(probe)) onContentFound(actionsColumnId);
    };

    checkForContent();
    return observeContent(probe, checkForContent);
  }, [actionsColumnId, onContentFound]);

  return (
    <div ref={probeRef} hidden aria-hidden>
      {dataSource.map((record, index) => (
        <Fragment key={index}>
          {renderActionsCell(column, record, index)}
        </Fragment>
      ))}
    </div>
  );
};

/**
 * Generic behavior for every table's row-actions column (any column whose
 * `className` includes `actions`):
 * - pinned to the right edge (`fixed: 'right'`) unless the column sets its
 *   own `fixed`, so the actions stay reachable on horizontal scroll;
 * - hidden altogether when no row on the current page renders anything in
 *   it (e.g. every dropdown item is gated by a permission the user lacks),
 *   and shown again as soon as any row does.
 *
 * "Renders anything" is decided from the actual DOM output, so it works no
 * matter how a table builds its actions (inline dropdown, a dedicated
 * dropdown component, plain buttons) as long as an empty row renders
 * nothing - `CustomDropdown` already returns null when it has no items.
 */
const useActionsColumn = (
  tableRef: React.RefObject<HTMLDivElement>,
  columns: AnyColumn[],
  dataSource: readonly any[] | undefined,
) => {
  const [emptyActionsColumnIds, setEmptyActionsColumnIds] = useState<
    string[]
  >([]);

  const markActionsColumnEmpty = useCallback((actionsColumnId: string) => {
    setEmptyActionsColumnIds(prev =>
      prev.includes(actionsColumnId) ? prev : [...prev, actionsColumnId],
    );
  }, []);

  const markActionsColumnHasContent = useCallback(
    (actionsColumnId: string) => {
      setEmptyActionsColumnIds(prev =>
        prev.includes(actionsColumnId)
          ? prev.filter(id => id !== actionsColumnId)
          : prev,
      );
    },
    [],
  );

  const actionsColumns = columns
    .map((column, index) =>
      isActionsColumn(column)
        ? { column, actionsColumnId: getActionsColumnId(column, index) }
        : null,
    )
    .filter(Boolean) as { column: AnyColumn; actionsColumnId: string }[];

  const visibleActionsColumnIds = actionsColumns
    .map(({ actionsColumnId }) => actionsColumnId)
    .filter(id => !emptyActionsColumnIds.includes(id));
  const visibleActionsColumnIdsKey = visibleActionsColumnIds.join(' ');

  // Watch the real (visible) actions cells - once every rendered row's cell
  // is empty, hide the column. No rows at all (loading / empty page) leaves
  // the current state alone.
  useEffect(() => {
    const table = tableRef.current;
    if (!table || !visibleActionsColumnIdsKey) return;

    const checkVisibleActionsCells = () => {
      visibleActionsColumnIdsKey.split(' ').forEach(actionsColumnId => {
        const cells = Array.from(
          table.querySelectorAll(`tr.ant-table-row > td.${actionsColumnId}`),
        );
        if (!cells.length) return;

        if (cells.every(cell => !hasRenderedContent(cell))) {
          markActionsColumnEmpty(actionsColumnId);
        }
      });
    };

    checkVisibleActionsCells();
    return observeContent(table, checkVisibleActionsCells);
  }, [tableRef, visibleActionsColumnIdsKey, markActionsColumnEmpty]);

  const tableColumns = columns.reduce((acc, column, index) => {
    if (!isActionsColumn(column)) {
      acc.push(column);
      return acc;
    }

    const actionsColumnId = getActionsColumnId(column, index);
    if (emptyActionsColumnIds.includes(actionsColumnId)) return acc;

    acc.push({
      ...column,
      fixed: column.fixed ?? 'right',
      className: classNames(column.className, actionsColumnId),
    });
    return acc;
  }, [] as AnyColumn[]);

  const hiddenActionsColumnProbes = actionsColumns
    .filter(({ actionsColumnId }) =>
      emptyActionsColumnIds.includes(actionsColumnId),
    )
    .map(({ column, actionsColumnId }) => (
      <HiddenActionsColumnProbe
        key={actionsColumnId}
        column={column}
        actionsColumnId={actionsColumnId}
        dataSource={dataSource ?? []}
        onContentFound={markActionsColumnHasContent}
      />
    ));

  return { tableColumns, hiddenActionsColumnProbes };
};

export default useActionsColumn;
